import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { BackButton } from '@dynamika-shared/ui/BackButton/BackButton';
import {
  PatientCompletedStatuses,
  PatientUploadStatusEnum,
} from '@dynamika-shared/types/status';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Popover, Skeleton } from 'antd';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { CheckMarkIcon, EditIcon } from '@dynamika-shared/ui/icons';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { DatePicker } from '@dynamika-shared/ui/DatePicker/DatePicker';
import { useQCTaskPatientSlice } from '@restore/QCTaskPatient/QCTaskPatient.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { convertDateToUTC } from '@utils/DateTime';
import { twJoin } from '@dynamika-shared/utils/twMerge';
import { d7Format } from '@dynamika-shared/utils/date';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { EditableSiteSelect } from './components/EditableSiteSelect';

const HIDE_EDIT_SUBJECT_CODE = false;

const commonWrapperClasses = 'w-full h-full px-2 py-1 rounded text-black text-1624';

const readOnlyValueClasses =
  'flex min-w-0 items-center truncate rounded px-2 text-black text-1624';

const metadataLabelClasses =
  'flex h-[32px] pr-1 items-center whitespace-nowrap text-1624 text-dimGray';

const rightMetadataLabelClasses = `${metadataLabelClasses} border-l border-gray-200 pl-3`;

const fixedHeightStyle = { minHeight: '32px' };

const convertPattern = (pattern: string): string => {
  return `^${pattern.replace(/\*/g, '[A-Z]').replace(/%/g, '[0-9]')}$`;
};

const QcTaskPatientSkeleton = () => (
  <>
    <div className='flex gap-1'>
      <Skeleton.Input active={true} size={'small'} />
      <Skeleton.Input active={true} size={'small'} />
    </div>
    <div className='flex flex-col items-end gap-1'>
      <Skeleton.Input active={true} size={'small'} />
      <Skeleton.Input active={true} size={'small'} />
    </div>
  </>
);

export const QcTaskPatient: FC<{
  goToTasks: () => void;
  confirmChangeStatus: (status: PatientUploadStatusEnum) => void;
  directChangePatientStatus: (status: PatientUploadStatusEnum) => void;
}> = ({ goToTasks, confirmChangeStatus, directChangePatientStatus }) => {
  const [patientData, patientDataIsLoading, statusLoading] =
    useQCTaskPatientSlice(s => [s.data, s.loading, s.statusLoading]);

  const [pendingDob, setPendingDob] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const qcTaskPatientActions = useQCTaskPatientSlice((_, actions) => actions);

  const contextData = useClinicalTrialContextSlice(state => state.data);

  const [selectedSite, setSelectedSite] = useState<{
    id: string;
    code: string;
  } | null>(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState<string | null>(null);
  const [pendingSiteCode, setPendingSiteCode] = useState<string | null>(null); // for modal
  const [showSiteConfirmModal, setShowSiteConfirmModal] = useState(false);
  const [isEditingSite, setIsEditingSite] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dob, setDob] = useState('');

  const dobDate = dob ? new Date(`${dob}-01`) : null;
  const dobUTC = convertDateToUTC(dobDate);

  const [pendingPatientCode, setPendingPatientCode] = useState('');
  const [showPatientCodeConfirmModal, setShowPatientCodeConfirmModal] =
    useState(false);
  const [draftPatientCode, setDraftPatientCode] = useState('');
  const [hasEdited, setHasEdited] = useState(false);
  const [isEditingPatientCode, setIsEditingPatientCode] = useState(false);

  const handleDateChange = (date: Date | null) => {
    if (!date || !patientData?.dob) return;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const newDob = `${year}-${month}`;

    const originalDob = patientData.dob
      .split('.')
      .reverse()
      .slice(0, 2)
      .join('-');

    if (newDob !== originalDob) {
      setPendingDob(newDob);
      setShowConfirmModal(true);
    }
  };

  const site = useMemo(
    () =>
      contextData?.configuration?.sites?.find(
        ({ id }) => id === patientData?.siteId
      ),
    [contextData?.configuration?.sites, patientData?.siteId]
  );

  const siteOptions = useMemo(() => {
    return (
      contextData?.configuration?.sites?.map(site => ({
        value: site.code,
        label: site.code,
      })) ?? []
    );
  }, [contextData]);

  const patientCohort = useMemo(() => {
    return contextData?.configuration?.cohorts?.find(
      cohort => cohort.id === patientData?.cohortId
    );
  }, [contextData?.configuration?.cohorts, patientData?.cohortId]);

  const genderValue = patientData?.gender || '-';
  const patientCohortName = patientCohort?.name || '-';
  const patientCohortPattern = patientCohort?.patientCodePattern;

  const handleSiteChange = (value: string) => {
    const newlySelectedSite = contextData?.configuration?.sites?.find(
      site => site.code === value
    );

    if (!newlySelectedSite || newlySelectedSite.id === patientData?.siteId) {
      return;
    }
    setSelectedSite(newlySelectedSite);
    setPendingSiteCode(newlySelectedSite.code);
    setIsEditingSite(false);
    setShowSiteConfirmModal(true);
  };

  const handleConfirmSiteChange = () => {
    const clinicalTrialId = contextData?.configuration?.id as string;

    if (selectedSite && patientData?.id) {
      qcTaskPatientActions.updatePatientSite({
        clinicalTrialId,
        id: patientData.id,
        siteId: selectedSite.id,
      });
      setSelectedSiteCode(selectedSite.code);
    }
    setShowSiteConfirmModal(false);
    setSelectedSite(null);
  };

  const handleCancelSiteChange = () => {
    setShowSiteConfirmModal(false);
    setSelectedSite(null);
    setIsEditingSite(false);
  };

  const formattedDOB = useMemo(() => {
    if (!dob) return '';
    try {
      return d7Format(`${dob}-01`, 'MMM - yyyy');
    } catch {
      return '';
    }
  }, [dob]);

  useEffect(() => {
    if (patientData?.dob) {
      const parsedDate = `${patientData?.dob.split('.').reverse().join('-')}`;
      setDob(parsedDate); // yyyy-mm format for input
    }
  }, [patientData?.dob]);

  const handleConfirmDobChange = () => {
    const clinicalTrialId = contextData?.configuration?.id as string;
    if (patientData?.id && pendingDob && pendingDob !== dob) {
      setDob(pendingDob);
      qcTaskPatientActions.updatePatientDob({
        clinicalTrialId,
        id: patientData.id,
        dob: pendingDob,
      });
    }
    setShowConfirmModal(false);
    setPendingDob('');
  };

  const handleCancelDobChange = () => {
    setShowConfirmModal(false);
    setPendingDob('');
  };

  const disabledDate = useCallback((current: Date) => {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 124);
    const maxDate = new Date();
    return current < minDate || current > maxDate;
  }, []);

  const handlePickerOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !showConfirmModal) {
        setIsEditing(false);
      }
    },
    [showConfirmModal]
  );

  const handleConfirmPatientCodeChange = () => {
    const clinicalTrialId = contextData?.configuration?.id as string;
    if (patientData?.id && pendingPatientCode) {
      qcTaskPatientActions.updatePatientCode({
        clinicalTrialId,
        id: patientData.id,
        patientCode: pendingPatientCode,
      });
      setHasEdited(false);
      setPendingPatientCode('');
      setShowPatientCodeConfirmModal(false);
      setIsEditingPatientCode(false);
    }
  };

  const handleCancelPatientCodeChange = () => {
    setShowPatientCodeConfirmModal(false);
    setPendingPatientCode('');
    setDraftPatientCode(patientData?.patientCode ?? '');
    setHasEdited(false);
    setIsEditingPatientCode(false);
  };

  const handlePatientCodeChange = (val: string) => {
    setDraftPatientCode(val);
    setHasEdited(true);
  };

  useEffect(() => {
    if (!hasEdited) {
      setDraftPatientCode(patientData?.patientCode ?? '');
    }
  }, [patientData?.patientCode, hasEdited]);

  const isPatientCodeValid = useMemo(() => {
    if (!hasEdited) return true;
    if (!patientCohortPattern) return true;

    try {
      const regex = new RegExp(convertPattern(patientCohortPattern));
      return regex.test(draftPatientCode);
    } catch {
      return false;
    }
  }, [draftPatientCode, hasEdited, patientCohortPattern]);

  return (
    <div className='flex items-center justify-between gap-4 pt-2 pb-2'>
      {patientDataIsLoading ? (
        <QcTaskPatientSkeleton />
      ) : (
        <>
          <div className='info flex items-center gap-[.5rem]'>
            <BackButton action={goToTasks} />

            <h2 className='leading-40 text-left text-[3.6rem] font-semibold tracking-normal flex flex-col justify-center ml-[10px]'>
              {!isEditingPatientCode ? (
                <div className='flex items-center gap-2 h-[50px] mr-[20px]'>
                  <span className='leading-[48px]'>
                    {patientData?.patientCode}
                  </span>
                  {!HIDE_EDIT_SUBJECT_CODE && (
                    <EditIcon
                      className='cursor-pointer text-dimGray'
                      width={16}
                      onClick={() => {
                        setIsEditingPatientCode(true);
                        setDraftPatientCode(patientData?.patientCode ?? '');
                      }}
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className='flex items-center gap-[8px] h-[50px]'>
                    <Input
                      name='patientCode'
                      value={draftPatientCode}
                      onChange={e =>
                        handlePatientCodeChange(
                          (e.target.value ?? '').toUpperCase()
                        )
                      }
                      placeholder='Enter subject code'
                      className='D7-Input'
                      classNameInputWrapper='max-w-[250px] h-[32px] mb-[10px]'
                    />
                    <Button
                      onClick={() => {
                        setDraftPatientCode(patientData?.patientCode ?? '');
                        setHasEdited(false);
                        setIsEditingPatientCode(false);
                      }}
                      className='!text-[12px] w-[100px] h-[32px] px-3 py-0 rounded-[1px] mt-[20px] mr-[5px]'
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (
                          isPatientCodeValid &&
                          draftPatientCode !== patientData?.patientCode
                        ) {
                          setPendingPatientCode(draftPatientCode);
                          setShowPatientCodeConfirmModal(true);
                        } else {
                          setIsEditingPatientCode(false);
                        }
                      }}
                      disabled={!isPatientCodeValid}
                      type='primary'
                      className='!text-[12px] w-[100px] h-[32px] px-3 py-0 rounded-[1px] mt-[20px] mr-[20px]'
                    >
                      Save
                    </Button>
                  </div>

                  <div className='h-[18px]'>
                    {!isPatientCodeValid && patientCohortPattern && (
                      <p className='text-red-500 w-[400px] text-[12px] ml-1 mt-[20px] flex flex-col'>
                        <span>
                          The entered subject ID does not match the cohort
                          pattern.
                        </span>
                        <span>
                          Please enter the Subject ID like {patientCohortPattern}
                        </span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </h2>

            {patientData?.uploadStatus && (
              <div className='status flex items-center'>
                <StatusPill status={patientData.uploadStatus} className='m-0' />
                {!PatientCompletedStatuses.some(
                  s => s === patientData?.uploadStatus
                ) && (
                  <Popover
                    open={openDropdown}
                    onOpenChange={open => setOpenDropdown(open)}
                    placement='bottom'
                    content={
                      <div>
                        {[
                          PatientUploadStatusEnum.ONGOING,
                          PatientUploadStatusEnum.COMPLETED,
                          PatientUploadStatusEnum.WITHDRAWN,
                        ].map(status => (
                          <div
                            role='button'
                            key={status}
                            onKeyDown={() => null}
                            onClick={() => {
                              if (status === patientData?.uploadStatus) {
                                return;
                              }
                              const action =
                                status !== PatientUploadStatusEnum.ONGOING
                                  ? confirmChangeStatus
                                  : directChangePatientStatus;
                              action(status);
                            }}
                            className='flex cursor-pointer items-center rounded-full hover:bg-waterLight'
                          >
                            <StatusPill
                              className='my-1 hover:text-black'
                              status={status}
                              dotted
                              textCombiner={' '}
                            >
                              {status === patientData.uploadStatus && (
                                <CheckMarkIcon className='text-[24px]' />
                              )}
                            </StatusPill>
                          </div>
                        ))}
                      </div>
                    }
                    trigger='click'
                  >
                    <ChevronIcon
                      className='b-1 h-2 w-2 cursor-pointer rounded-full border text-[16px]'
                      classNameButton='h-3 w-3'
                      dir={openDropdown ? 'up' : 'down'}
                    />
                  </Popover>
                )}
              </div>
            )}
          </div>

          <div className='inline-grid w-fit shrink-0 grid-cols-[repeat(4,max-content)] items-center gap-y-1'>
            <div className='contents'>
              <div className='contents'>
                <span className={metadataLabelClasses}>Date of Birth:</span>

                <div
                  className='relative flex h-[32px] w-full items-center'
                  onClick={e => e.stopPropagation()}
                >
                  <div className='w-full h-full'>
                    {isEditing ? (
                      <DatePicker
                        picker='month'
                        value={dobUTC}
                        onChange={handleDateChange}
                        disabledDate={disabledDate}
                        format='MMM - yyyy'
                        placeholder='MMM - YYYY'
                        suffixIcon={null}
                        className={twJoin(
                          'border border-gray-300',
                          commonWrapperClasses
                        )}
                        style={fixedHeightStyle}
                        open={isEditing}
                        onOpenChange={handlePickerOpenChange}
                      />
                    ) : (
                      <div
                        className={twJoin(
                          'flex items-center justify-between gap-2',
                          'w-full h-[32px] px-2 rounded cursor-pointer text-black text-1624',
                          statusLoading && 'opacity-50 pointer-events-none',
                          !statusLoading &&
                            'hover:bg-blue-100 transition-colors duration-200'
                        )}
                        onClick={() => !statusLoading && setIsEditing(true)}
                      >
                        <span className='leading-[32px]'>{formattedDOB}</span>

                        <EditIcon color='dimGray' width={14} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='contents'>
                <span className={rightMetadataLabelClasses}>Site ID:</span>

                {isEditingSite ? (
                  <EditableSiteSelect
                    isOpen={isEditingSite}
                    value={selectedSiteCode ?? site?.code}
                    options={siteOptions}
                    onChange={handleSiteChange}
                    onClose={() => setIsEditingSite(false)}
                  />
                ) : (
                  <div
                    className={twJoin(
                      'inline-flex items-center gap-2',
                      'h-[32px] px-2 rounded cursor-pointer text-black text-1624',
                      'hover:bg-blue-100 transition-colors duration-200'
                    )}
                    onClick={e => {
                      e.stopPropagation();
                      setIsEditingSite(true);
                    }}
                  >
                    <span className='truncate leading-[32px] text-black'>
                      {selectedSiteCode ?? site?.code}
                    </span>

                    <EditIcon color='dimGray' width={14} />
                  </div>
                )}
              </div>
            </div>

            <div className='contents'>
              <div className='contents'>
                <span className={metadataLabelClasses}>Gender:</span>

                <span className={readOnlyValueClasses} title={genderValue}>
                  {genderValue}
                </span>
              </div>

              <div className='contents'>
                <span className={rightMetadataLabelClasses}>Cohort:</span>

                <span
                  className={readOnlyValueClasses}
                  title={patientCohortName}
                >
                  {patientCohortName}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        width={440}
        centered
        maskClosable={true}
        mask={true}
        titleClassName='text-left'
        data-testid={'unsaved-chan  ges-popup'}
        title='Confirm Date of Birth Change'
        open={showConfirmModal}
        onOk={handleConfirmDobChange}
        onCancel={handleCancelDobChange}
        okText='Confirm'
        cancelText='Cancel'
      >
        <p>
          Are you sure you want to change the Date of Birth to{' '}
          <strong>{d7Format(`${pendingDob}-01`, 'MMM - yyyy')}</strong>?
        </p>
      </Modal>

      <Modal
        width={440}
        centered
        maskClosable={true}
        mask={true}
        titleClassName='text-left'
        data-testid={'unsaved-chan  ges-popup'}
        title='Confirm Site Change'
        open={showSiteConfirmModal}
        onOk={handleConfirmSiteChange}
        onCancel={handleCancelSiteChange}
        okText='Confirm'
        cancelText='Cancel'
      >
        <p>
          Are you sure you want to change the Site ID to{' '}
          <strong>{pendingSiteCode}</strong> ?
        </p>
      </Modal>

      <Modal
        width={440}
        centered
        maskClosable={true}
        mask={true}
        titleClassName='text-left'
        data-testid={'unsaved-chan  ges-popup'}
        title='Confirm Patient Code Change'
        open={showPatientCodeConfirmModal}
        onOk={handleConfirmPatientCodeChange}
        onCancel={handleCancelPatientCodeChange}
        okText='Confirm'
        cancelText='Cancel'
      >
        <p>
          Are you sure you want to assign the Subject Code:{' '}
          <strong>{pendingPatientCode}</strong> ?
        </p>
      </Modal>
    </div>
  );
};
